import { useCallback, useEffect, useRef, useState } from "react";
import { Room, RoomEvent, Track } from "livekit-client";
import { requestVoiceToken, newVoiceRoom, type VoiceToken } from "./voiceApi";

export type VoiceState = "idle" | "connecting" | "connected" | "reconnecting" | "error";

export interface UseVoiceSessionArgs {
  /** Voice service base url; the default token fetch authenticates via the
   *  cookie session (credentials are sent same-origin). */
  baseUrl?: string;
  /** Resolve a Bearer token for the token fetch (embed userJWT). Omitted in
   *  the SPA where the session cookie carries auth. */
  getAuthToken?: () => string | Promise<string>;
  /** Injectable seams for tests. */
  createRoom?: () => Room;
  requestToken?: (room: string) => Promise<VoiceToken>;
}

export interface VoiceSession {
  state: VoiceState;
  audioLevel: number;
  /** True while the active speaker is the assistant (a remote participant),
   *  not the local user. Drives Bolt's talking expression in voice mode. */
  assistantSpeaking: boolean;
  isMuted: boolean;
  error: string | null;
  connect: () => Promise<void>;
  toggleMute: () => Promise<void>;
  end: () => Promise<void>;
}

export function useVoiceSession(args: UseVoiceSessionArgs = {}): VoiceSession {
  const [state, setState] = useState<VoiceState>("idle");
  const [audioLevel, setAudioLevel] = useState(0);
  const [assistantSpeaking, setAssistantSpeaking] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const roomRef = useRef<Room | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const connectingRef = useRef(false);
  const mountedRef = useRef(true);

  // Fix 3: stabilise deps — only the specific fields requestToken uses.
  const requestToken = useCallback(
    async (room: string): Promise<VoiceToken> => {
      if (args.requestToken) return args.requestToken(room);
      const authToken = args.getAuthToken ? await args.getAuthToken() : undefined;
      return requestVoiceToken({ baseUrl: args.baseUrl ?? "", room, authToken });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [args.requestToken, args.baseUrl, args.getAuthToken],
  );

  const connect = useCallback(async () => {
    // Fix 1: guard against a second concurrent connect (mic-leak prevention).
    if (connectingRef.current || roomRef.current) return;
    connectingRef.current = true;

    setError(null);
    setState("connecting");
    try {
      const roomName = newVoiceRoom();
      const tok = await requestToken(roomName);
      // Fix 3: use args.createRoom directly (stable ref field).
      const room = args.createRoom ? args.createRoom() : new Room();
      roomRef.current = room;

      // Fix 2: gate post-unmount setStates in async event handlers.
      room.on(RoomEvent.Disconnected, () => { if (mountedRef.current) { setState("idle"); setAssistantSpeaking(false); } });
      room.on(RoomEvent.Reconnecting, () => { if (mountedRef.current) setState("reconnecting"); });
      room.on(RoomEvent.Reconnected, () => { if (mountedRef.current) setState("connected"); });
      room.on(RoomEvent.TrackSubscribed, (track: any) => {
        if (track?.kind === Track.Kind.Audio) {
          // Fix 5: remove any prior audio element before attaching the new one.
          if (audioElRef.current) {
            audioElRef.current.remove();
          }
          const el = track.attach() as HTMLAudioElement;
          el.autoplay = true;
          audioElRef.current = el;
          document.body.appendChild(el);
        }
      });
      room.on(RoomEvent.ActiveSpeakersChanged, (speakers: any[]) => {
        if (mountedRef.current) {
          const top = speakers && speakers.length ? speakers[0] : null;
          setAudioLevel(top?.audioLevel ?? 0);
          // The assistant is a remote (agent) participant; the user is local.
          // Only treat a remote active speaker as "assistant speaking".
          setAssistantSpeaking(!!top && top.isLocal === false && (top.audioLevel ?? 0) > 0.1);
        }
      });

      await room.connect(tok.url, tok.token);
      await room.localParticipant.setMicrophoneEnabled(true);
      setIsMuted(false);
      setState("connected");
    } catch (e) {
      setError(e instanceof Error ? e.message : "voice connection failed");
      setState("error");
    } finally {
      // Fix 1: always clear the in-flight guard.
      connectingRef.current = false;
    }
  // Fix 3: dep is args.createRoom (stable field ref), not the whole args object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [args.createRoom, requestToken]);

  const toggleMute = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    const next = !isMuted;
    setIsMuted(next);
    // Fix 4: revert UI state if the mic call throws.
    try {
      await room.localParticipant.setMicrophoneEnabled(!next);
    } catch {
      setIsMuted(!next);
    }
  }, [isMuted]);

  const end = useCallback(async () => {
    const room = roomRef.current;
    if (room) await room.disconnect();
    if (audioElRef.current) { audioElRef.current.remove(); audioElRef.current = null; }
    roomRef.current = null;
    setAudioLevel(0);
    setAssistantSpeaking(false);
    setState("idle");
  }, []);

  // Always tear down on unmount — never leak a live mic/room.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      // Fix 2: stop post-unmount setState from async event handlers.
      mountedRef.current = false;
      const room = roomRef.current;
      if (room) room.disconnect();
      if (audioElRef.current) audioElRef.current.remove();
    };
  }, []);

  return { state, audioLevel, assistantSpeaking, isMuted, error, connect, toggleMute, end };
}
